/* Marzelle — quiet interactions. The `js` class is added inline in theme.liquid. */
(() => {
  const root = window.Shopify?.routes?.root || '/';
  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';
  const focusables = (el) => [...el.querySelectorAll(FOCUSABLE)].filter((n) => n.offsetParent !== null || n === document.activeElement);
  const trapTab = (e, container) => {
    if (e.key !== 'Tab') return;
    const items = focusables(container);
    if (!items.length) { e.preventDefault(); return; }
    const first = items[0];
    const last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || !container.contains(document.activeElement))) {
      e.preventDefault(); last.focus();
    } else if (!e.shiftKey && (document.activeElement === last || !container.contains(document.activeElement))) {
      e.preventDefault(); first.focus();
    }
  };

  // ---------- Toast (errors only) ----------
  const toast = document.getElementById('CartToast');
  let toastTimer;
  const showToast = (message) => {
    if (!toast) return;
    const text = toast.querySelector('[data-toast-text]') || toast;
    text.textContent = message;
    toast.classList.add('is-visible');
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => toast.classList.remove('is-visible'), 4800);
  };

  // ---------- Header: scrolled state ----------
  const header = document.querySelector('.site-header');
  if (header) {
    let ticking = false;
    const update = () => { header.classList.toggle('is-scrolled', window.scrollY > 8); ticking = false; };
    update();
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; window.requestAnimationFrame(update); }
    }, { passive: true });
  }

  // ---------- Mobile menu ----------
  const menu = document.getElementById('MobileMenu');
  let menuOpener = null;
  const menuToggles = () => document.querySelectorAll('[data-menu-toggle]');
  const setMenu = (open, opener) => {
    if (!menu) return;
    menu.classList.toggle('is-open', open);
    menu.setAttribute('aria-hidden', String(!open));
    document.body.style.overflow = open ? 'hidden' : '';
    menuToggles().forEach((b) => b.setAttribute('aria-expanded', String(open)));
    if (open) {
      menuOpener = opener || document.activeElement;
      const first = menu.querySelector('nav a') || focusables(menu)[0];
      window.setTimeout(() => first?.focus({ preventScroll: true }), 60);
    } else if (menuOpener) {
      const target = menuOpener.closest('#MobileMenu')
        ? document.querySelector('.site-header [data-menu-toggle]')
        : menuOpener;
      target?.focus({ preventScroll: true });
      menuOpener = null;
    }
  };
  const menuIsOpen = () => !!menu && menu.classList.contains('is-open');
  if (menu) {
    menu.setAttribute('aria-hidden', 'true');
    menuToggles().forEach((btn) => {
      btn.addEventListener('click', () => setMenu(!menuIsOpen(), btn));
    });
    menu.addEventListener('keydown', (e) => { if (menuIsOpen()) trapTab(e, menu); });
    menu.querySelectorAll('nav a').forEach((a) => a.addEventListener('click', () => {
      menuOpener = null; // navigating; do not pull focus back
      setMenu(false);
    }));
  }

  // ---------- Reveal on scroll ----------
  const reveals = document.querySelectorAll('.reveal');
  if ('IntersectionObserver' in window) {
    // In the mobile carousels, cards off to the right would still be hidden
    // while swiping, so a carousel's cards reveal together as one group.
    const groups = new Map();
    const inCarousel = window.matchMedia('(max-width: 760px)').matches;
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          (groups.get(entry.target) || [entry.target]).forEach((el) => el.classList.add('is-visible'));
          io.unobserve(entry.target);
        }
      });
    }, { rootMargin: '0px 0px -8% 0px' });
    reveals.forEach((el) => {
      const track = inCarousel && el.closest('.products-grid--carousel, .pieces--row');
      if (!track) { io.observe(el); return; }
      if (!groups.has(track)) { groups.set(track, []); io.observe(track); }
      groups.get(track).push(el);
    });
  } else {
    reveals.forEach((el) => el.classList.add('is-visible'));
  }

  // ---------- Quantity steppers ----------
  document.querySelectorAll('[data-qty]').forEach((wrap) => {
    const input = wrap.querySelector('input');
    if (!input) return;
    wrap.querySelectorAll('button[data-step]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const step = Number(btn.dataset.step);
        const min = Number(input.min || 0);
        const max = input.max ? Number(input.max) : Infinity;
        input.value = Math.min(max, Math.max(min, Number(input.value || 0) + step));
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    });
  });

  // ---------- Product gallery ----------
  document.querySelectorAll('[data-gallery]').forEach((gallery) => {
    const main = gallery.querySelector('.product__main img');
    const thumbs = [...gallery.querySelectorAll('[data-thumb]')];
    const count = gallery.querySelector('[data-gallery-count]');
    if (!main || !thumbs.length) return;
    let current = Math.max(0, thumbs.findIndex((t) => t.getAttribute('aria-current') === 'true'));
    const preloaded = new Set();
    const preload = (index) => {
      const t = thumbs[(index + thumbs.length) % thumbs.length];
      if (!t || preloaded.has(t)) return;
      preloaded.add(t);
      const img = new Image();
      img.sizes = '(min-width: 900px) 58vw, 100vw';
      if (t.dataset.srcset) img.srcset = t.dataset.srcset;
      img.src = t.dataset.src;
    };
    const show = (index) => {
      current = (index + thumbs.length) % thumbs.length;
      const thumb = thumbs[current];
      main.sizes = '(min-width: 900px) 58vw, 100vw';
      main.srcset = thumb.dataset.srcset || '';
      main.src = thumb.dataset.src;
      main.alt = thumb.dataset.alt || main.alt;
      thumbs.forEach((t) => t.setAttribute('aria-current', 'false'));
      thumb.setAttribute('aria-current', 'true');
      if (count) count.textContent = `${current + 1} / ${thumbs.length}`;
    };
    const warmNeighbours = () => { preload(current + 1); preload(current - 1); };
    thumbs.forEach((thumb, i) => {
      thumb.addEventListener('click', () => show(i));
      thumb.addEventListener('pointerenter', () => preload(i), { passive: true });
    });
    gallery.addEventListener('pointerenter', warmNeighbours, { passive: true });
    gallery.addEventListener('touchstart', warmNeighbours, { passive: true });
    gallery.querySelector('[data-gallery-prev]')?.addEventListener('click', () => { show(current - 1); warmNeighbours(); });
    gallery.querySelector('[data-gallery-next]')?.addEventListener('click', () => { show(current + 1); warmNeighbours(); });
    gallery.addEventListener('keydown', (e) => {
      if (e.key === 'ArrowLeft') { show(current - 1); warmNeighbours(); }
      if (e.key === 'ArrowRight') { show(current + 1); warmNeighbours(); }
    });
    const frame = gallery.querySelector('.product__main');
    if (frame) {
      let startX = null;
      frame.addEventListener('touchstart', (e) => { startX = e.touches[0].clientX; }, { passive: true });
      frame.addEventListener('touchend', (e) => {
        if (startX === null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) { show(current + (dx < 0 ? 1 : -1)); warmNeighbours(); }
        startX = null;
      });
    }
  });

  // ---------- Variant selection ----------
  const setButtonState = (btn, available, labels) => {
    if (!btn) return;
    btn.disabled = !available;
    const add = btn.dataset.addLabel || labels.add;
    const sold = btn.dataset.soldLabel || labels.sold;
    const label = available ? add : sold;
    if (!label) return;
    const span = btn.querySelector('span');
    if (span) span.textContent = label; else btn.textContent = label;
  };
  document.querySelectorAll('[data-product-form]').forEach((form) => {
    const json = form.querySelector('[data-variants]');
    if (!json) return;
    let variants = [];
    try { variants = JSON.parse(json.textContent); } catch (err) { return; }
    const idInput = form.querySelector('input[name="id"]');
    const submit = form.querySelector('[type="submit"]');
    const labels = { add: submit?.dataset.addLabel, sold: submit?.dataset.soldLabel };
    form.addEventListener('change', (e) => {
      if (!e.target.closest('.option-group')) return;
      const chosen = [...form.querySelectorAll('.option-group')].map((g) => g.querySelector('input:checked')?.value);
      const match = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      if (!match) return;
      if (idInput) idInput.value = match.id;
      setButtonState(submit, match.available, labels);
      document.querySelectorAll('[data-sticky-add]').forEach((b) => setButtonState(b, match.available, labels));
      document.querySelectorAll('[data-price]').forEach((el) => { el.textContent = match.price; });
      const url = new URL(window.location.href);
      url.searchParams.set('variant', match.id);
      window.history.replaceState({}, '', url);
    });
  });

  // ---------- Cart drawer ----------
  const drawerEl = () => document.getElementById('CartDrawer');
  let drawerOpener = null;
  const drawerIsOpen = () => !!drawerEl()?.classList.contains('is-open');
  const openDrawer = () => {
    const d = drawerEl(); if (!d) return;
    if (!drawerIsOpen()) drawerOpener = document.activeElement;
    d.classList.add('is-open');
    d.setAttribute('aria-hidden', 'false');
    document.body.style.overflow = 'hidden';
    const close = d.querySelector('.drawer__panel [data-drawer-close]') || d.querySelector('[data-drawer-close]');
    window.setTimeout(() => close?.focus({ preventScroll: true }), 60);
  };
  const closeDrawer = () => {
    const d = drawerEl(); if (!d || !drawerIsOpen()) return;
    d.classList.remove('is-open');
    d.setAttribute('aria-hidden', 'true');
    document.body.style.overflow = menuIsOpen() ? 'hidden' : '';
    if (drawerOpener && document.contains(drawerOpener)) drawerOpener.focus({ preventScroll: true });
    drawerOpener = null;
  };
  const updateCounts = (n) => {
    document.querySelectorAll('[data-cart-count]').forEach((c) => { c.textContent = n; c.dataset.count = n; });
  };
  async function refreshDrawer(open) {
    const res = await fetch(`${root}?sections=cart-drawer`);
    if (!res.ok) throw new Error('refresh');
    const data = await res.json();
    const html = new DOMParser().parseFromString(data['cart-drawer'], 'text/html');
    const fresh = html.getElementById('CartDrawer');
    const current = drawerEl();
    if (fresh && current) {
      const wasOpen = current.classList.contains('is-open');
      const hadFocus = current.contains(document.activeElement);
      current.innerHTML = fresh.innerHTML;
      if (wasOpen) {
        current.classList.add('is-open');
        current.setAttribute('aria-hidden', 'false');
        if (hadFocus) current.querySelector('.drawer__panel [data-drawer-close]')?.focus({ preventScroll: true });
      }
    }
    try {
      const cart = await (await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } })).json();
      updateCounts(cart.item_count);
    } catch (err) { /* counts refresh on next page load */ }
    if (open) openDrawer();
  }

  // AJAX add to bag; falls back to a normal form post without JS
  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('form[action$="/cart/add"]');
    if (!form || !window.fetch) return;
    event.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.disabled = true;
    try {
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });
      if (!res.ok) {
        let message = '';
        try { message = (await res.json()).description; } catch (err) { /* ignore */ }
        throw new Error(message || "Couldn't add to your bag. Please try again.");
      }
      if (drawerEl()) {
        await refreshDrawer(true);
      } else {
        window.location.href = `${root}cart`;
      }
    } catch (err) {
      showToast(err && err.message && err.message !== 'refresh' ? err.message : "Couldn't add to your bag. Please try again.");
    } finally {
      if (btn) btn.disabled = false;
    }
  });

  document.addEventListener('click', async (e) => {
    if (e.target.closest('[data-drawer-close]')) { closeDrawer(); return; }
    const cartLink = e.target.closest('a.site-header__bag, a[href$="/cart"].icon-btn');
    if (cartLink && drawerEl() && !document.querySelector('form[data-cart-autosubmit]')) {
      e.preventDefault(); openDrawer(); return;
    }
    const change = e.target.closest('[data-line-change]');
    if (change) {
      const d = drawerEl();
      d?.classList.add('is-loading');
      try {
        const res = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: change.dataset.lineChange, quantity: Number(change.dataset.qty) }),
        });
        if (!res.ok) {
          let message = '';
          try { message = (await res.json()).description; } catch (err) { /* ignore */ }
          throw new Error(message || "Couldn't update your bag. Please try again.");
        }
        await refreshDrawer(false);
      } catch (err) {
        showToast(err && err.message && err.message !== 'refresh' ? err.message : "Couldn't update your bag. Please try again.");
      } finally {
        drawerEl()?.classList.remove('is-loading');
      }
    }
  });

  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') {
      if (drawerIsOpen()) closeDrawer();
      else if (menuIsOpen()) setMenu(false);
      return;
    }
    if (e.key === 'Tab' && drawerIsOpen()) {
      const panel = drawerEl().querySelector('.drawer__panel');
      if (panel) trapTab(e, panel);
    }
  });

  // ---------- Cart page: update quantities without a button ----------
  const cartForm = document.querySelector('form[data-cart-autosubmit]');
  if (cartForm) {
    let cartTimer;
    cartForm.addEventListener('change', (e) => {
      if (!e.target.matches('input[name="updates[]"]')) return;
      clearTimeout(cartTimer);
      cartTimer = setTimeout(() => cartForm.submit(), 400);
    });
  }

  // ---------- Order note autosave (drawer + cart page) ----------
  let noteTimer;
  document.addEventListener('input', (e) => {
    if (e.target.id !== 'DrawerNote' && e.target.id !== 'CartNote') return;
    const value = e.target.value;
    clearTimeout(noteTimer);
    noteTimer = setTimeout(() => {
      fetch(`${root}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ note: value }),
      }).catch(() => {});
    }, 500);
  });

  // ---------- Sticky add to bag (mobile PDP) ----------
  const sticky = document.querySelector('[data-sticky-buy]');
  const buyRow = document.querySelector('[data-buy-row]');
  if (sticky && buyRow && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      const show = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', show);
      sticky.setAttribute('aria-hidden', String(!show));
      sticky.querySelectorAll('a, button').forEach((el) => { el.tabIndex = show ? 0 : -1; });
    }).observe(buyRow.hidden ? (buyRow.closest('form') || buyRow.parentElement) : buyRow);
    sticky.querySelector('[data-sticky-add]')?.addEventListener('click', (e) => {
      const form = buyRow.closest('form');
      if (!form) return;
      e.preventDefault();
      const s = form.querySelector('[type=submit]');
      if (s && s.disabled) return;
      if (form.requestSubmit) form.requestSubmit(s || undefined); else s?.click();
    });
  }

  // ---------- Contact form: prefill from ?subject= ----------
  const contactForm = document.querySelector('form[data-contact-form]');
  if (contactForm) {
    const subject = new URLSearchParams(window.location.search).get('subject');
    const body = document.getElementById('ContactBody');
    if (subject && body && !body.value.trim()) {
      body.value = `I'd like to request: ${subject}\n\n`;
    }
  }
})();
